import { ProcessedEvent, getCleanEventTitle } from './TaskSchedulerUtils'
import { secToTime, toISOStringYYYYMMDD } from './TaskSchedulerUtils'

export const exportScheduleToCSV = (filteredCalendarEvents: ProcessedEvent[]) => {
  if (!filteredCalendarEvents.length) {
    return
  }

  // Define headers
  const headers = [
    'Date',
    'Time',
    'Robot',
    'Task ID',
    'Task Name',
    'Status',
    'Exception Reason',
    'Excepted By',
    'Mission', // Mission ID(s)
    'Cleaning Performance (m²/h)',
    'Progress (%)',
    'Cleaning Area (m²)',
    'Duration',
    'Battery Used (%)',
  ]

  // Map events to rows
  const rows = [...filteredCalendarEvents]
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map(e => {
      const date = toISOStringYYYYMMDD(e.start)
      const time = e.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      const props = e.extendedProps || {}

      // Determine status if not explicitly set (fallback)
      const status = props.status || (String(e.event_id).includes('stat') ? 'executed' : 'scheduled')

      // Clean title removes the icons we added
      let cleanTitle = getCleanEventTitle(e.title)

      let robotSn = props.robot_sn || ''
      let taskId = props.task_id || ''

      const parts = cleanTitle.split(' | ')
      // Format: Robot [name] | Task [id] | [name]
      if (parts.length >= 3) {
        // Part 0: "Robot 3020000001"
        const robotPart = parts[0].trim()
        if (robotPart.startsWith('Robot ')) {
          // If props.robot_sn is missing, try to parse it
          if (!robotSn) {
            robotSn = robotPart.replace('Robot ', '').trim()
          }
        }

        // Part 1: "Task 25"
        const taskPart = parts[1].trim()
        if (taskPart.startsWith('Task ')) {
          if (!taskId) {
            taskId = taskPart.replace('Task ', '').trim()
          }
        }

        // Part 2: "Cleaning Task"
        cleanTitle = parts[2].trim()
      } else if (parts.length > 0) {
        cleanTitle = parts[parts.length - 1].trim()
      }

      // Statistics Data extraction
      let missionIds = ''
      let missionName = ''
      let cleaningPerformance = ''
      let progress = ''
      let cleaningArea = ''
      let durationStr = ''
      let batteryUsed = ''

      if (status === 'executed') {
        // Handle Missions
        if (props.missions) {
          const ongoingMissions = Array.isArray(props.missions) ? props.missions : [props.missions]
          missionIds = ongoingMissions.join(', ')
        } else if (props.mission_id) {
          missionIds = String(props.mission_id)
        }

        // Handle Mission Name (only if single mission or explicitly provided)
        if (props.mission_name) {
          missionName = props.mission_name
        }

        const statsSource = props.overall_progress || props.mission_progress

        if (statsSource) {
          if (statsSource.cleaning_performance > 0.01) {
            cleaningPerformance = statsSource.cleaning_performance.toFixed(1)
          } else if (statsSource.duration_actual > 0) {
            cleaningPerformance = ((statsSource.cleaned_area_actual / statsSource.duration_actual) * 3600).toFixed(1)
          }

          let prog = (statsSource.travel_distance_actual / statsSource.travel_distance_planned) * 100.0
          if (prog > 100.0) prog = 100.0
          if (prog) progress = prog.toFixed(1)

          if (statsSource.cleaned_area_planned) cleaningArea = statsSource.cleaned_area_planned.toFixed(1)

          if (statsSource.duration_actual) durationStr = secToTime(statsSource.duration_actual)

          if (statsSource.battery_begin && statsSource.battery_end) {
            const val = statsSource.battery_begin - statsSource.battery_end
            if (val > 0) batteryUsed = val.toFixed(1)
          }
        }
      }

      return [
        date,
        time,
        robotSn,
        taskId,
        cleanTitle,
        status,
        props.exception_reason || '',
        props.exception_user || '',
        missionIds,
        cleaningPerformance,
        progress,
        cleaningArea,
        durationStr,
        batteryUsed,
      ]
        .map(cell => `"${String(cell).replace(/"/g, '""')}"`)
        .join(',') // CSV escaping
    })

  // Join and download
  const csvContent = [headers.join(','), ...rows].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `schedule_export_${toISOStringYYYYMMDD(new Date())}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
